import React, { createContext, useCallback, useContext, useState } from 'react';
import { NmToast } from '../components/NmToast';

interface ToastContextValue {
  showToast: (message: string, variant?: 'success' | 'error' | 'info') => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Global toast — use for calendar add/delete feedback and form success. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);

  const showToast = useCallback((text: string, _variant?: 'success' | 'error' | 'info') => {
    setMessage(text);
  }, []);
  const success = useCallback((text: string) => showToast(text, 'success'), [showToast]);
  const error = useCallback((text: string) => showToast(text, 'error'), [showToast]);
  const info = useCallback((text: string) => showToast(text, 'info'), [showToast]);

  const dismiss = useCallback(() => setMessage(null), []);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info }}>
      {children}
      {message ? <NmToast message={message} onClose={dismiss} /> : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

/** Extract a user-facing message from unknown thrown values (axios-aware). */
export function toastErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const responseData = (error as {
      response?: {
        data?: {
          message?: unknown;
          errors?: Record<string, string[] | string>;
        };
      };
    }).response?.data;

    if (responseData) {
      const apiMessage = responseData.message;
      if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
      }
      const validationErrors = responseData.errors;
      if (validationErrors && typeof validationErrors === 'object') {
        for (const value of Object.values(validationErrors)) {
          if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
            return value[0];
          }
          if (typeof value === 'string' && value.trim()) {
            return value;
          }
        }
      }
    }

    const msg = (error as { message?: unknown }).message;
    if (
      typeof msg === 'string'
      && msg.trim()
      && !/^Request failed with status code \d+$/.test(msg)
    ) {
      return msg;
    }
  }
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
}
